/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Integraciones: Mercado Pago + Facturación electrónica.
 * Hub de navegación con TicketListRow (una sola representación del estado, #1463).
 */
import React, { useCallback, useEffect } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { RefreshControl, StyleSheet } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { useBranchSettingsScreen } from './useBranchSettingsScreen';

type IntegrationRow = {
  key: string;
  title: string;
  description: string;
  onPress?: () => void;
  disabled?: boolean;
  statusLabel: string;
  statusTone: 'ok' | 'warn' | 'bad' | 'muted';
  iconName: string;
};

export function IntegrationsHubScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const vm = useBranchSettingsScreen();
  const { layoutWide } = useTabWorkspaceLayout();
  const navigation = useNavigation<any>();

  const { refreshing, onRefresh } = useScreenLoadShell('IntegrationsHub', async (opts) => {
    await vm.load(opts);
    await vm.refreshArcaIntegrationStatus({ force: opts?.force === true });
  });

  useFocusEffect(
    useCallback(() => {
      void vm.refreshArcaIntegrationStatus({ force: true });
    }, [vm.refreshArcaIntegrationStatus])
  );

  const containerStyle = layoutWide
    ? [styles.container, styles.containerWide]
    : [styles.container, styles.containerCompact];

  // Sin KPI strip: las filas ya muestran el estado real de cada integración
  // (y "Solo lectura" cuando falta permiso). El strip repetía exactamente las
  // mismas filas — una sola representación (#1463, ref #483).

  // —— Guards inline (sin BranchSettingsHubShell) ——

  if (!vm.currentBranchId) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Integraciones"
          title="Seleccioná una sucursal"
          subtitle="Necesitás una sucursal activa para ver las integraciones."
          primaryAction={{ label: 'Ir al selector', onPress: vm.handleOpenBranchSelector }}
        />
      </ShellContentFrame>
    );
  }

  if (vm.loading) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState loading loadingText="Cargando integraciones…" />
      </ShellContentFrame>
    );
  }

  if (!vm.canViewIntegrations) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Integraciones"
          title="Sin permiso"
          subtitle="No tenés permiso para ver las integraciones de este local."
          primaryAction={{
            label: 'Volver',
            onPress: () => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile' as never)),
          }}
        />
      </ShellContentFrame>
    );
  }

  // —— Filas de integraciones ——

  const rows: IntegrationRow[] = [
    vm.mercadoPagoIntegrationsEnabled
      ? {
          key: 'mp',
          title: 'Mercado Pago',
          description: vm.canManageMp
            ? vm.mpOAuthConnected
              ? vm.mpPosConfigured
                ? 'Cuenta conectada y caja QR.'
                : 'Cuenta conectada. Elegí la caja de este local.'
              : 'Conectá tu cuenta Mercado Pago.'
            : 'Solo lectura. Pedile al dueño que te habilite esto.',
          onPress: vm.canManageMp ? vm.handleOpenMercadoPago : undefined,
          disabled: !vm.canManageMp,
          statusLabel: vm.canManageMp
            ? vm.mpOAuthConnected
              ? vm.mpPosConfigured
                ? 'Conectada'
                : 'Sin caja'
              : 'Configurar'
            : 'Solo lectura',
          statusTone: vm.canManageMp
            ? vm.mpOAuthConnected
              ? vm.mpPosConfigured
                ? 'ok'
                : 'warn'
              : 'muted'
            : 'warn',
          iconName: 'card-outline',
        }
      : null,
    vm.arcaIntegrationsEnabled
      ? {
          key: 'arca',
          title: 'Facturación electrónica',
          description: vm.canManageFiscal
            ? vm.arcaSetupValidated === true
              ? vm.arcaPvValidated === false
                ? vm.arcaPointOfSale == null
                  ? 'ARCA está conectada. A este local le falta el punto de venta.'
                  : 'ARCA está conectada. Falta verificar el punto de venta de este local.'
                : vm.arcaEnvironment === 'produccion'
                  ? 'Certificado y punto de venta listos para emitir.'
                  : 'Estás en modo prueba. Los comprobantes no valen.'
              : 'Certificados, puntos de venta y facturas.'
            : 'Solo lectura. Pedile al dueño que te habilite esto.',
          onPress: vm.canManageFiscal ? vm.handleOpenArcaIntegration : undefined,
          disabled: !vm.canManageFiscal,
          statusLabel: vm.canManageFiscal
            ? !vm.arcaStatusLoaded
              ? '…'
              : vm.arcaSetupValidated === true
                ? vm.arcaPvValidated === false
                  ? 'Falta este local'
                  : vm.arcaEnvironment === 'produccion'
                    ? 'Activa'
                    : 'En prueba'
                : 'Configurar'
            : 'Solo lectura',
          statusTone: vm.canManageFiscal
            ? vm.arcaSetupValidated === true
              ? vm.arcaPvValidated === false
                ? 'warn'
                : vm.arcaEnvironment === 'produccion'
                  ? 'ok'
                  : 'warn'
              : 'muted'
            : 'warn',
          iconName: 'document-text-outline',
        }
      : null,
  ].filter(Boolean) as IntegrationRow[];

  return (
    <ShellContentFrame style={containerStyle}>
      <ModalAwareScrollView
        contentContainerStyle={styles.scroll}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={ticketColors.ink2}
            colors={[ticketColors.ink2]}
          />
        }
      >
        <TicketModuleHeader
          kicker="Integraciones"
          title="Conectá tu local"
          meta="Mercado Pago y facturación electrónica"
          testID="integrations.header"
        />

        {rows.length > 0 ? (
          <PaperSheet padded={false} style={styles.listSheet}>
            {rows.map((row, index) => (
              <TicketListRow
                key={row.key}
                title={row.title}
                subtitle={row.description}
                statusLabel={row.statusLabel}
                statusTone={row.statusTone}
                onPress={row.onPress ?? (() => undefined)}
                inactive={row.disabled}
                showChevron={!row.disabled}
                accessibilityLabel={
                  row.disabled ? `${row.title} (sin permiso)` : `Abrir ${row.title}`
                }
                group={
                  rows.length === 1
                    ? 'solo'
                    : index === 0
                      ? 'grouped'
                      : index === rows.length - 1
                        ? 'groupedLast'
                        : 'grouped'
                }
                testID={`config.integrations.${row.key}`}
              />
            ))}
          </PaperSheet>
        ) : (
          <ModuleEmptyState
            variant="nest"
            kicker="Integraciones"
            title="Sin integraciones habilitadas"
            subtitle="Este plan no tiene integraciones habilitadas. Actualizá tu plan para acceder a Mercado Pago y facturación electrónica."
            testID="integrations.empty"
          />
        )}
      </ModalAwareScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    ...ticketSurfaces.screen,
  },
  containerWide: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  containerCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  scroll: {
    gap: spacing.md,
    paddingBottom: spacing.xxl,
  },
  listSheet: {
    paddingVertical: spacing.xs,
  },
});
